import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFormStore } from '../../store/formStore'
import { logout } from '../../api/applicant'
import { useSession } from '../../apply/session'
import { Button } from '../ui/Button'

export function NavBar() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // Hide the nav button across the My Applications area: on the list itself it
  // would just link to the current page (the in-page "Start new application"
  // action covers that screen), and on an application's detail view the
  // "‹ My Applications" back button already returns there.
  const onApplications = pathname === '/applications' || pathname.startsWith('/applications/')
  const authEmailFromData = useFormStore((s) => s.formData['authEmail'] as string | undefined)
  const authEmailFromStore = useFormStore((s) => s.authEmail)
  const { account } = useSession()
  // The session account (from the backend) is the source of truth; the store
  // values only bridge the moment before /auth/me resolves.
  const authEmail = account?.email || authEmailFromData || authEmailFromStore || ''
  const reset = useFormStore((s) => s.reset)
  const { clearSession } = useSession()
  const [appsHovered, setAppsHovered] = useState(false)

  async function handleLogout() {
    try {
      await logout()
    } catch {
      // Logout is idempotent server-side; clear the client state regardless.
    }
    reset()
    clearSession()
    navigate('/login')
  }

  return (
    <header
      className="sticky top-0 flex items-center justify-between px-6 md:px-8"
      style={{
        height: '72px',
        zIndex: 'var(--z-stepper)' as string,
        background: 'var(--color-surface-glass)',
        backdropFilter: 'var(--glass-standard-blur)',
        WebkitBackdropFilter: 'var(--glass-standard-blur)',
        borderBottom: '1px solid var(--color-outline-hairline)',
      }}
    >
      {/* Logo */}
      <img
        src={`${import.meta.env.BASE_URL}zota-logo.svg`}
        alt="Zota"
        style={{ height: '24px', width: 'auto', display: 'block' }}
      />

      {/* Right section */}
      <div
        className="flex items-center gap-4"
        style={{ fontFamily: 'var(--font-body)' }}
      >
        {authEmail && (
          <span
            className="hidden sm:block"
            style={{
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-secondary)',
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={authEmail}
          >
            {authEmail}
          </span>
        )}
        {authEmail && !onApplications && (
          <button
            onClick={() => navigate('/applications')}
            onMouseEnter={() => setAppsHovered(true)}
            onMouseLeave={() => setAppsHovered(false)}
            style={{
              position: 'relative',
              overflow: 'hidden',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '36px',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-text-on-primary)',
              background: 'var(--color-action-primary)',
              border: 'none',
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: '24px',
              fontFamily: 'var(--font-body)',
              fontWeight: 'var(--weight-semibold)' as unknown as number,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {/* Hover: blue→light-blue gradient (Figma node 318:36) fades in over the solid blue. */}
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: 'var(--gradient-action)',
                opacity: appsHovered ? 1 : 0,
                transition: 'opacity var(--duration-standard) var(--ease-out)',
                pointerEvents: 'none',
              }}
            />
            <span style={{ position: 'relative', zIndex: 1 }}>My applications</span>
          </button>
        )}
        {/* Keeps the tertiary tap target (45px) while sitting flush with the
            nav's own edge: the side padding is cancelled by an equal negative
            side margin, exactly as the variant already cancels its vertical
            padding. Dropping either half moves the label — measured, the label
            edge stays put and only the target grows. */}
        <Button
          variant="tertiary"
          onClick={handleLogout}
          style={{ margin: 'calc(var(--spacing-1) - var(--spacing-3)) calc(-1 * var(--spacing-2))' }}
        >
          Log out
        </Button>
      </div>
    </header>
  )
}
